/**
 * @fileoverview BlogList.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { blogArticles } from '../utils/blogData'
import SEO from '../components/ui/SEO'
import { IMG } from '../assets/Assets'

function BlogList() {
  return (
    <div className='flex flex-col w-full min-h-screen pb-20 text-left pt-24 md:pt-32 bg-gray-50'>
      <SEO 
        title="Blog"
        description="Read KITH Travels' travel guides, hiking tips, and local insights for visiting Sri Lanka. Explore the Pekoe Trail, wildlife safaris, and essential travel tips."
        keywords="Sri Lanka travel blog, Sri Lanka travel guides, travel tips Sri Lanka, Pekoe trail guide, best safaris Sri Lanka"
      />
      
      {/* Page Header */}
      <div className='px-[5%] md:px-[10%]'>
        <div className='relative w-full h-[40vh] rounded-[2.5rem] overflow-hidden shadow-xl flex items-center justify-center bg-gray-900'>
          <img 
            src={IMG.SL_Culinary} 
            alt="Sri Lanka Travel Guides & Blog" 
            className='absolute inset-0 w-full h-full object-cover opacity-50'
          />
          <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent'></div>
          <div className='relative z-10 text-center flex flex-col gap-4 max-w-2xl px-6 animate-fadeIn'>
            <span className='text-KITH-primary font-bold tracking-[0.2em] uppercase text-xs'>Travel Guides & Stories</span>
            <h1 className='text-3xl md:text-5xl font-black text-white leading-tight'>KITH Travels Blog</h1>
            <p className='text-white/80 text-sm md:text-base'>
              Expert local advice, travel tips, and stories from the heart of Sri Lanka.
            </p>
          </div>
        </div>
      </div>

      {/* Grid Section */}
      <div className='px-[5%] md:px-[10%] mt-20'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10'>
          {blogArticles.map((article, idx) => (
            <article key={idx} className='bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between group'>
              <Link to={`/blog/${article.slug}`} className='relative aspect-[16/10] overflow-hidden block'>
                <img 
                  src={article.image} 
                  alt={article.title} 
                  className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
                  loading="lazy"
                />
                <div className='absolute bottom-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-gray-700 shadow-sm'>
                  {article.readingTime}
                </div>
              </Link>
              <div className='p-6 md:p-8 flex flex-col gap-4 flex-grow justify-between text-left'>
                <div className='flex flex-col gap-2'>
                  <span className='text-[10px] text-gray-400 font-bold uppercase tracking-wider'>
                    {new Date(article.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                  <h2 className='text-xl font-bold text-gray-800 leading-tight group-hover:text-KITH-primary transition-colors'>
                    <Link to={`/blog/${article.slug}`}>
                      {article.title}
                    </Link>
                  </h2>
                  <p className='text-gray-500 text-xs md:text-sm leading-relaxed line-clamp-3'>
                    {article.summary}
                  </p>
                </div>
                <div className='border-t border-gray-50 pt-4 flex justify-between items-center mt-2'>
                  <span className='text-[10px] text-gray-400 font-bold'>By {article.author}</span>
                  <Link to={`/blog/${article.slug}`} className='text-KITH-primary font-bold text-xs flex items-center gap-1 hover:underline'>
                    Read Article <i className='fi fi-br-arrow-right text-[10px] mt-[1px]'></i>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  )
}

export default BlogList
